import type { InputPeriod } from '~/composables/useSalaryConverter'

export type { InputPeriod }

export const SALARY_CONVERTER_DEFAULTS = {
  salary: 60000,
  period: 'annually' as InputPeriod
} as const

export const VALID_INPUT_PERIODS = ['annually', 'monthly', 'biweekly', 'weekly', 'daily', 'hourly'] as const

// Maximum salary allowed (10 billion)
export const MAX_SALARY = 10000000000

/**
 * Parse salary from URL parameter
 */
export function parseSalary(salaryParam: string | undefined | null): number {
  if (!salaryParam) return SALARY_CONVERTER_DEFAULTS.salary
  const parsed = parseInt(salaryParam, 10)
  if (isNaN(parsed) || parsed <= 0 || parsed > MAX_SALARY) {
    return SALARY_CONVERTER_DEFAULTS.salary
  }
  return parsed
}

/**
 * Parse pay period from URL parameter
 */
export function parsePeriod(periodParam: string | undefined | null): InputPeriod {
  if (!periodParam) return SALARY_CONVERTER_DEFAULTS.period
  const normalized = periodParam.toLowerCase()
  if (VALID_INPUT_PERIODS.includes(normalized as InputPeriod)) {
    return normalized as InputPeriod
  }
  return SALARY_CONVERTER_DEFAULTS.period
}

/**
 * Parse salary from URL format - supports decimals via dash (e.g., "16-50" = 16.50)
 */
function parseSalaryFromUrl(salaryPart: string): number | null {
  // Check if it contains a decimal dash (e.g., "16-50" for 16.50)
  const decimalMatch = salaryPart.match(/^(\d+)-(\d{1,2})$/)
  if (decimalMatch) {
    const whole = parseInt(decimalMatch[1], 10)
    const decimal = decimalMatch[2].padEnd(2, '0') // Ensure 2 digits
    const salary = parseFloat(`${whole}.${decimal}`)
    if (!isNaN(salary) && salary > 0 && salary <= MAX_SALARY) {
      return salary
    }
    return null
  }

  // Regular integer salary
  const salary = parseInt(salaryPart, 10)
  if (!isNaN(salary) && salary > 0 && salary <= MAX_SALARY) {
    return salary
  }
  return null
}

/**
 * Parse the combined period-salary parameter (e.g., "annually-60000" or "hourly-16-50")
 */
export function parsePeriodSalary(param: string | undefined | null): { period: InputPeriod; salary: number } {
  if (!param) {
    return { period: SALARY_CONVERTER_DEFAULTS.period, salary: SALARY_CONVERTER_DEFAULTS.salary }
  }

  const dashIndex = param.indexOf('-')
  if (dashIndex === -1) {
    // Try to parse as salary only (backwards compatibility)
    const salary = parseInt(param, 10)
    if (!isNaN(salary) && salary > 0) {
      return { period: SALARY_CONVERTER_DEFAULTS.period, salary }
    }
    return { period: SALARY_CONVERTER_DEFAULTS.period, salary: SALARY_CONVERTER_DEFAULTS.salary }
  }

  const periodPart = param.substring(0, dashIndex).toLowerCase()
  const salaryPart = param.substring(dashIndex + 1)

  const period = VALID_INPUT_PERIODS.includes(periodPart as InputPeriod)
    ? (periodPart as InputPeriod)
    : SALARY_CONVERTER_DEFAULTS.period

  const salary = parseSalaryFromUrl(salaryPart)
  const validSalary = salary !== null ? salary : SALARY_CONVERTER_DEFAULTS.salary

  return { period, salary: validSalary }
}

/**
 * Validate if a URL segment is a valid period-salary format (e.g., "annually-60000" or "hourly-16-50")
 */
export function isValidPeriodSalaryFormat(segment: string): boolean {
  const dashIndex = segment.indexOf('-')
  if (dashIndex === -1) {
    return false
  }

  const periodPart = segment.substring(0, dashIndex).toLowerCase()
  const salaryPart = segment.substring(dashIndex + 1)

  if (!VALID_INPUT_PERIODS.includes(periodPart as InputPeriod)) {
    return false
  }

  const salary = parseSalaryFromUrl(salaryPart)
  return salary !== null
}

/**
 * Format salary for URL - uses dash for decimals (e.g., 16.50 becomes "16-50")
 */
function formatSalaryForUrl(salary: number): string {
  // Check if salary has decimal places
  if (salary % 1 !== 0) {
    const [whole, decimal] = salary.toFixed(2).split('.')
    // Remove trailing zeros from decimal, but keep at least one digit
    const cleanDecimal = decimal.replace(/0+$/, '') || '0'
    return `${whole}-${cleanDecimal}`
  }
  return Math.round(salary).toString()
}

/**
 * Build salary converter URL
 */
export function buildSalaryConverterUrl(options: {
  salary?: number
  period?: InputPeriod
} = {}): string {
  const salary = options.salary && options.salary > 0
    ? options.salary
    : SALARY_CONVERTER_DEFAULTS.salary

  const period = options.period && VALID_INPUT_PERIODS.includes(options.period)
    ? options.period
    : SALARY_CONVERTER_DEFAULTS.period

  return `/salary-converter/${period}-${formatSalaryForUrl(salary)}`
}

/**
 * Build salary converter URL - short version (no params if defaults)
 */
export function buildSalaryConverterUrlShort(options: {
  salary?: number
  period?: InputPeriod
} = {}): string {
  const salary = options.salary && options.salary > 0
    ? options.salary
    : null

  const period = options.period && VALID_INPUT_PERIODS.includes(options.period)
    ? options.period
    : SALARY_CONVERTER_DEFAULTS.period

  if (!salary) {
    return '/salary-converter'
  }

  return `/salary-converter/${period}-${formatSalaryForUrl(salary)}`
}
